import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged, signInWithCustomToken, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { auth, db, firestorePathId, functions, isE2EEnvironment } from '../services/firebase';
import { getUnidadeServidor } from '../constants/servidorApiFieldMap';
import { buildMunicipioLoginUrl, getStoredMunicipioId, persistMunicipioId } from '../utils/municipioContext';
import { clearNotificationWorkerOwner, revokeCurrentNotificationToken } from '../services/notificationService';

const AuthContext = createContext(null);

const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
};

const cleanCpf = (value) => String(value || '').replace(/\D/g, '').slice(0, 11);

const getFunctionErrorMessage = (error) => {
  const code = String(error?.code || '');

  if (
    code.includes('unauthenticated') ||
    code.includes('invalid-argument') ||
    code.includes('permission-denied')
  ) {
    return error?.message || 'CPF, senha ou município inválidos.';
  }

  return 'Erro ao tentar fazer login. Tente novamente.';
};

const isTerminalSessionRestoreError = (error) => {
  const code = String(error?.code || '').toLowerCase();
  return [
    'unauthenticated',
    'permission-denied',
    'invalid-argument',
    'user-disabled',
    'user-token-expired',
    'id-token-revoked',
    'invalid-user-token'
  ].some((terminalCode) => code.includes(terminalCode));
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [vinculos, setVinculos] = useState([]);
  const [vinculoSelecionado, setVinculoSelecionado] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [unidadesDicionario, setUnidadesDicionario] = useState({});

  const loginInProgress = useRef(false);
  const unidadesRef = useRef({});
  const municipioConfigRef = useRef({ campoSeparacaoUnidadeServidor: 'automatico' });
  const initialAuthResolved = useRef(false);

  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  const enrichVinculo = useCallback((vinculo, dict = unidadesRef.current) => {
    if (!vinculo) return vinculo;

    const idGestora = vinculo.unidgest_id !== undefined && vinculo.unidgest_id !== null
      ? String(vinculo.unidgest_id)
      : '';

    const unidadeConfigurada = getUnidadeServidor(vinculo, municipioConfigRef.current);
    const nomeLotacao = unidadeConfigurada !== 'Sem unidade informada'
      ? unidadeConfigurada
      : dict?.[idGestora] || vinculo.lotacao || vinculo.secretaria || idGestora || 'Não identificada';

    return {
      ...vinculo,
      lotacao: nomeLotacao,
      secretaria: nomeLotacao
    };
  }, []);

  const setCleanUnauthenticatedState = useCallback(() => {
    // Mesmo sem rede, uma sessão encerrada não deve continuar autorizando o SW
    // a exibir notificações destinadas ao usuário anterior deste navegador.
    clearNotificationWorkerOwner().catch(() => {});
    municipioConfigRef.current = { campoSeparacaoUnidadeServidor: 'automatico' };
    setUser(null);
    setVinculos([]);
    setVinculoSelecionado(null);
    setIsAuthenticated(false);
    setAuthReady(false);
  }, []);

  const loadUnidadesGestoras = useCallback(async () => {
    if (Object.keys(unidadesRef.current || {}).length > 0) {
      return unidadesRef.current;
    }

    try {
      const response = await fetch('/data/unidgest.tsv', { cache: 'force-cache' });

      if (!response.ok) {
        unidadesRef.current = {};
        setUnidadesDicionario({});
        return {};
      }

      const text = await response.text();
      const dict = {};

      text.split('\n').forEach((line) => {
        const parts = line.split('\t');
        if (parts.length >= 2) {
          const id = String(parts[0] || '').trim();
          const nome = String(parts[1] || '').trim();
          if (id && nome) dict[id] = nome;
        }
      });

      unidadesRef.current = dict;
      setUnidadesDicionario(dict);
      return dict;
    } catch (error) {
      unidadesRef.current = {};
      setUnidadesDicionario({});
      return {};
    }
  }, []);

  const restoreSessionFromStorage = useCallback((authUser) => {
    const sessionData = safeJsonParse(sessionStorage.getItem('servidorSessionData'), null);

    if (!sessionData?.user || sessionData.user.uid !== authUser.uid) {
      return false;
    }

    const normalizedUser = {
      ...sessionData.user,
      cpf: cleanCpf(sessionData.user.cpf)
    };

    const dict = unidadesRef.current || {};
    const restoredVinculos = Array.isArray(sessionData.vinculos)
      ? sessionData.vinculos.map((item) => enrichVinculo(item, dict))
      : [];

    const restoredVinculoSelecionado = sessionData.vinculoSelecionado
      ? enrichVinculo(sessionData.vinculoSelecionado, dict)
      : null;

    setUser(normalizedUser);
    setVinculos(restoredVinculos);
    setVinculoSelecionado(restoredVinculoSelecionado);
    setIsAuthenticated(true);

    sessionStorage.setItem('servidorSessionData', JSON.stringify({
      ...sessionData,
      user: normalizedUser,
      vinculos: restoredVinculos,
      vinculoSelecionado: restoredVinculoSelecionado
    }));

    return true;
  }, [enrichVinculo]);

  const applyValidatedSession = useCallback(async ({ authUser, responseData, previousSessionData }) => {
    if (!responseData?.success || !responseData.userData) {
      const invalidResponse = new Error('A restauração da sessão retornou dados inválidos.');
      invalidResponse.code = 'functions/permission-denied';
      throw invalidResponse;
    }

    const normalizedCpf = cleanCpf(responseData.userData.cpf);
    const municipioId = String(responseData.userData.municipioId || '');
    const userData = {
      ...responseData.userData,
      uid: responseData.userData.uid || authUser.uid,
      cpf: normalizedCpf,
      municipioId,
      mustChangePassword: responseData.mustChangePassword ?? responseData.userData.mustChangePassword ?? false,
      serverPortalPermissions: responseData.userData.serverPortalPermissions || {},
      logoUrl: responseData.userData.logoUrl || ''
    };

    if (normalizedCpf.length !== 11 || !municipioId || userData.uid !== authUser.uid) {
      const scopeError = new Error('A sessão restaurada não corresponde ao usuário autenticado.');
      scopeError.code = 'functions/permission-denied';
      throw scopeError;
    }

    const dict = await loadUnidadesGestoras();
    const todosOsVinculos = Array.isArray(responseData.vinculos)
      ? responseData.vinculos.map((item) => enrichVinculo(item, dict))
      : [];

    if (todosOsVinculos.length === 0) {
      const noLinksError = new Error('Nenhum vínculo ativo ou liberado encontrado para este CPF.');
      noLinksError.code = 'functions/permission-denied';
      throw noLinksError;
    }

    const previousSelected = previousSessionData?.vinculoSelecionado;
    const previousSelectedId = String(previousSelected?.id || '');
    let selectedVinculo = previousSelectedId
      ? todosOsVinculos.find((item) => String(item?.id || '') === previousSelectedId) || null
      : null;

    if (!selectedVinculo && todosOsVinculos.length === 1) {
      selectedVinculo = todosOsVinculos[0];
    }

    const sessionData = {
      user: userData,
      vinculos: todosOsVinculos,
      ...(selectedVinculo ? { vinculoSelecionado: selectedVinculo } : {})
    };

    sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));
    persistMunicipioId(municipioId);
    setUser(userData);
    setVinculos(todosOsVinculos);
    setVinculoSelecionado(selectedVinculo);
    setIsAuthenticated(true);
    setAuthReady(true);
  }, [enrichVinculo, loadUnidadesGestoras]);

  const logout = useCallback(async (message = 'Sessão encerrada com segurança.') => {
    if (loginInProgress.current) {
      return;
    }

    if (auth.currentUser?.uid) {
      await revokeCurrentNotificationToken({ reason: 'logout' }).catch((error) => {
        console.warn('Não foi possível concluir a revogação remota do push no logout:', error);
      });
    }

    sessionStorage.removeItem('servidorSessionData');
    setCleanUnauthenticatedState();

    try {
      await signOut(auth);
    } catch (error) {
      console.error('Erro ao encerrar sessão:', error);
    }

    const savedMunicipioId = getStoredMunicipioId();

    const loginUrl = savedMunicipioId ? new URL(buildMunicipioLoginUrl(savedMunicipioId)) : new URL('/login', window.location.origin);
    if (message) loginUrl.searchParams.set('message', message);
    window.location.href = loginUrl.toString();
  }, [setCleanUnauthenticatedState]);


  const setTermoResponsabilidadeAceito = useCallback((aceite = {}) => {
    setUser((currentUser) => {
      if (!currentUser) return currentUser;

      const updatedUser = {
        ...currentUser,
        termoResponsabilidadeAceitoVersao: Number(aceite.versao || 0),
        termoResponsabilidadeAceitoVersaoId: String(aceite.versaoId || ''),
        termoResponsabilidadeAceitoHashConteudo: String(
          aceite.hashConteudo || ''
        ),
      };

      const sessionData = safeJsonParse(
        sessionStorage.getItem('servidorSessionData'),
        {}
      );
      sessionData.user = updatedUser;
      sessionStorage.setItem(
        'servidorSessionData',
        JSON.stringify(sessionData)
      );

      return updatedUser;
    });
  }, []);

  const selecionarVinculo = useCallback((vinculo) => {
    const vinculoNormalizado = enrichVinculo(vinculo);

    setVinculoSelecionado(vinculoNormalizado);

    const sessionData = safeJsonParse(sessionStorage.getItem('servidorSessionData'), {});
    sessionData.vinculoSelecionado = vinculoNormalizado;
    sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));
  }, [enrichVinculo]);

  const limparVinculoSelecionado = useCallback(() => {
    setVinculoSelecionado(null);

    const sessionData = safeJsonParse(sessionStorage.getItem('servidorSessionData'), {});
    delete sessionData.vinculoSelecionado;
    sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));
  }, []);

  const setPasswordChanged = useCallback(() => {
    setUser((currentUser) => {
      if (!currentUser) return null;

      const updatedUser = { ...currentUser, mustChangePassword: false };
      const sessionData = safeJsonParse(sessionStorage.getItem('servidorSessionData'), {});
      sessionData.user = updatedUser;
      sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));

      return updatedUser;
    });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (loginInProgress.current) {
        return;
      }

      let restored = false;

      try {
        if (!authUser) {
          sessionStorage.removeItem('servidorSessionData');
          setCleanUnauthenticatedState();
          return;
        }

        if (auth.currentUser?.uid !== authUser.uid) {
          return;
        }

        const previousSessionData = safeJsonParse(sessionStorage.getItem('servidorSessionData'), null);
        restored = restoreSessionFromStorage(authUser);

        await authUser.getIdToken();

        const loginServidorPorCpf = httpsCallable(functions, 'loginServidorPorCpf');
        const response = await loginServidorPorCpf({ restaurarSessao: true });

        if (auth.currentUser?.uid !== authUser.uid) {
          return;
        }

        await applyValidatedSession({
          authUser,
          responseData: response?.data || {},
          previousSessionData
        });
      } catch (error) {
        if (isTerminalSessionRestoreError(error)) {
          await revokeCurrentNotificationToken({ reason: 'session-revoked' }).catch(() => {});
          sessionStorage.removeItem('servidorSessionData');
          setCleanUnauthenticatedState();
          await signOut(auth).catch(() => {});
          console.warn('Sessão do servidor revogada, inativa ou fora do escopo:', error);
        } else if (!restored) {
          setCleanUnauthenticatedState();
          setAuthError('Não foi possível restaurar sua sessão agora. Atualize a página e tente novamente.');
          console.warn('Falha técnica temporária durante a restauração da sessão:', error);
        } else {
          setAuthReady(true);
          console.warn('Validação remota da sessão indisponível; sessão Firebase preservada:', error);
        }
      } finally {
        if (!initialAuthResolved.current) {
          initialAuthResolved.current = true;
          setLoading(false);
        }
      }
    });

    return () => unsubscribe();
  }, [applyValidatedSession, restoreSessionFromStorage, setCleanUnauthenticatedState]);

  useEffect(() => {
    if (!authReady || !isAuthenticated || !user?.municipioId) return undefined;

    const municipalityDocRef = doc(db, `artifacts/${firestorePathId}/public/data/municipalities/${user.municipioId}`);

    const unsubscribePermissions = onSnapshot(municipalityDocRef, (docSnap) => {
      if (!docSnap.exists()) return;

      const municipalityData = docSnap.data();
      const newPermissions = municipalityData.serverPortalPermissions || {};
      const newLogoUrl = municipalityData.logoUrl || '';
      const newMunicipioNome = municipalityData.name || municipalityData.nome || municipalityData.municipioNome || municipalityData.razaoSocial || '';
      const newCampoSeparacao = municipalityData.campoSeparacaoUnidadeServidor || 'automatico';
      const newTermoResponsabilidadeAtual = municipalityData.termoResponsabilidadeAtual || null;
      const campoSeparacaoChanged = municipioConfigRef.current.campoSeparacaoUnidadeServidor !== newCampoSeparacao;

      municipioConfigRef.current = {
        campoSeparacaoUnidadeServidor: newCampoSeparacao
      };

      if (campoSeparacaoChanged) {
        setVinculos((currentVinculos) => currentVinculos.map((item) => enrichVinculo(item)));
        setVinculoSelecionado((currentVinculo) => currentVinculo ? enrichVinculo(currentVinculo) : null);

        const sessionData = safeJsonParse(sessionStorage.getItem('servidorSessionData'), {});
        sessionData.vinculos = Array.isArray(sessionData.vinculos)
          ? sessionData.vinculos.map((item) => enrichVinculo(item))
          : [];
        sessionData.vinculoSelecionado = sessionData.vinculoSelecionado
          ? enrichVinculo(sessionData.vinculoSelecionado)
          : null;
        sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));
      }

      if (newMunicipioNome) {
        try {
          window.localStorage.setItem(`sisdp_server_municipio_name:${user.municipioId}`, newMunicipioNome);
        } catch (storageError) {
          // A identidade municipal continua disponível na sessão mesmo se o storage estiver bloqueado.
        }
      }

      setUser((currentUser) => {
        if (!currentUser) return currentUser;

        const samePermissions = JSON.stringify(currentUser.serverPortalPermissions || {}) === JSON.stringify(newPermissions);
        const sameLogo = (currentUser.logoUrl || '') === newLogoUrl;
        const sameMunicipioNome = (currentUser.municipioNome || '') === newMunicipioNome;
        const sameCampoSeparacao = (currentUser.campoSeparacaoUnidadeServidor || 'automatico') === newCampoSeparacao;
        const sameTermoResponsabilidade = JSON.stringify(currentUser.termoResponsabilidadeAtual || null) === JSON.stringify(newTermoResponsabilidadeAtual || null);

        if (samePermissions && sameLogo && sameMunicipioNome && sameCampoSeparacao && sameTermoResponsabilidade) return currentUser;

        const updatedUser = {
          ...currentUser,
          serverPortalPermissions: newPermissions,
          logoUrl: newLogoUrl,
          municipioNome: newMunicipioNome,
          campoSeparacaoUnidadeServidor: newCampoSeparacao,
          termoResponsabilidadeAtual: newTermoResponsabilidadeAtual
        };

        const sessionData = safeJsonParse(sessionStorage.getItem('servidorSessionData'), {});
        sessionData.user = updatedUser;
        sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));

        return updatedUser;
      });
    });

    return () => unsubscribePermissions();
  }, [authReady, enrichVinculo, isAuthenticated, user?.municipioId]);

  const login = useCallback(async (cpf, password, municipioId) => {
    loginInProgress.current = true;
    setAuthError(null);

    const cleanedCpf = cleanCpf(cpf);

    try {
      if (auth.currentUser?.uid) {
        await revokeCurrentNotificationToken({ reason: 'new-login' }).catch((error) => {
          console.warn('Não foi possível concluir a revogação do push da sessão anterior:', error);
        });
      }
      await signOut(auth).catch(() => {});

      const loginServidorPorCpf = httpsCallable(functions, 'loginServidorPorCpf');
      const response = await loginServidorPorCpf({
        cpf: cleanedCpf,
        senha: String(password || ''),
        municipioId
      });

      const responseData = response?.data || {};

      if (!responseData.success || !responseData.customToken || !responseData.userData) {
        const errorMessage = 'CPF, senha ou município inválidos.';
        setAuthError(errorMessage);
        return { success: false, message: errorMessage };
      }

      const userCredential = isE2EEnvironment
        ? await signInWithEmailAndPassword(auth, responseData.e2eAuth?.email, String(password || ''))
        : await signInWithCustomToken(auth, responseData.customToken);
      const firebaseUid = userCredential.user.uid;

      await userCredential.user.getIdToken(true);
      await userCredential.user.getIdTokenResult(true);

      if (auth.currentUser?.uid !== firebaseUid) {
        throw new Error('Sessão Firebase não ficou pronta após o login.');
      }

      persistMunicipioId(municipioId);

      const userData = {
        ...responseData.userData,
        uid: responseData.userData.uid || firebaseUid,
        cpf: cleanCpf(responseData.userData.cpf || cleanedCpf),
        municipioId,
        mustChangePassword: responseData.mustChangePassword ?? responseData.userData.mustChangePassword ?? false,
        serverPortalPermissions: responseData.userData.serverPortalPermissions || {},
        logoUrl: responseData.userData.logoUrl || ''
      };

      const dict = await loadUnidadesGestoras();
      const todosOsVinculos = Array.isArray(responseData.vinculos)
        ? responseData.vinculos.map((item) => enrichVinculo(item, dict))
        : [];

      if (todosOsVinculos.length === 0) {
        await signOut(auth).catch(() => {});
        const errorMessage = 'Nenhum vínculo ativo ou liberado encontrado para este CPF.';
        setAuthError(errorMessage);
        return { success: false, message: errorMessage };
      }

      const sessionData = {
        user: userData,
        vinculos: todosOsVinculos
      };

      let selectedVinculo = null;

      if (todosOsVinculos.length === 1) {
        selectedVinculo = todosOsVinculos[0];
        sessionData.vinculoSelecionado = selectedVinculo;
      }

      sessionStorage.setItem('servidorSessionData', JSON.stringify(sessionData));

      setUser(userData);
      setVinculos(todosOsVinculos);
      setVinculoSelecionado(selectedVinculo);
      setIsAuthenticated(true);
      setAuthReady(true);
      setLoading(false);

      return {
        success: true,
        userData,
        mustChangePassword: userData.mustChangePassword,
        needsVinculoSelection: todosOsVinculos.length > 1
      };
    } catch (error) {
      if (error && typeof error === 'object') {
        error.sisdpLogContext = {
          ...(error.sisdpLogContext && typeof error.sisdpLogContext === 'object' ? error.sisdpLogContext : {}),
          module: 'auth',
          operation: 'login_by_cpf',
          functionName: 'loginServidorPorCpf',
          category: 'authentication'
        };
      }
      console.error('Erro no processo de login:', error);
      await signOut(auth).catch(() => {});
      setCleanUnauthenticatedState();

      const errorMessage = getFunctionErrorMessage(error);
      setAuthError(errorMessage);

      return { success: false, message: errorMessage };
    } finally {
      window.setTimeout(() => {
        loginInProgress.current = false;
      }, 600);
    }
  }, [enrichVinculo, loadUnidadesGestoras, setCleanUnauthenticatedState]);

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    authReady,
    loading,
    vinculos,
    userLinks: vinculos,
    vinculoSelecionado,
    unidadesDicionario,
    login,
    logout,
    setPasswordChanged,
    setTermoResponsabilidadeAceito,
    selecionarVinculo,
    limparVinculoSelecionado,
    authError,
    clearAuthError
  }), [
    user,
    isAuthenticated,
    authReady,
    loading,
    vinculos,
    vinculoSelecionado,
    unidadesDicionario,
    login,
    logout,
    setPasswordChanged,
    setTermoResponsabilidadeAceito,
    selecionarVinculo,
    limparVinculoSelecionado,
    authError,
    clearAuthError
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider.');
  }

  return context;
};
